import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  HStack,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import {
  CloudDownload as CloudDownloadIcon,
  Folder as FolderIcon,
} from "@mui/icons-material";
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import {
  GhostButton,
  SymbolOutlineButton,
} from "~/src/components/common/Button";
import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormControlGroup } from "~/src/components/common/forms/FormControlGroup";
import { Modal } from "~/src/components/common/Modal";
import { chakraFactory } from "~/src/lib/chakra-ui";

import {
  FUNNEL_IMPORT_COLUMN_COUNT_WITHOUT_ACTION,
  FUNNEL_IMPORT_COLUMN_COUNT_WITH_ACTION,
  IMPORT_MAX_FUNNELS,
  downloadImportTemplate,
  parseCsvToFormValues,
} from "../../FunnelImport/csvUtls";

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  siteSlug: string;
};

const StyledSection = chakraFactory(Box, {
  baseStyle: {
    mb: "24px",
  },
});

const StyledFileName = chakraFactory(Text, {
  baseStyle: {
    fontSize: "13px",
    color: "#4A5568",
    ml: "8px",
  },
});

const StyledLimitText = chakraFactory(Text, {
  baseStyle: {
    fontSize: "12px",
    color: "#718096",
    mt: "4px",
  },
});

export const FunnelCsvUploadModal: FC<Props> = ({
  isOpen,
  onClose,
  siteSlug,
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState("");
  const [csvErrors, setCsvErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setCsvErrors([]);
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [isOpen]);

  const handleTemplateDownload = useCallback(() => {
    downloadImportTemplate();
  }, []);

  const handleClickFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setFileName(file.name);
      setCsvErrors([]);

      const { rows, errors: parseErrors } = await parseCsvToFormValues(file);
      if (parseErrors.length > 0) {
        setCsvErrors(parseErrors);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      navigate(`/sites/${siteSlug}/funnels/import`, {
        state: { funnels: rows },
      });
      onClose();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [navigate, onClose, siteSlug]
  );

  return (
    <Modal title="CSVアップロード" isOpen={isOpen} onClose={onClose}>
      <StyledSection>
        <FormControlGroup>
          <FormControl as="div" label="テンプレート">
            <SymbolOutlineButton
              icon={<CloudDownloadIcon />}
              onClick={handleTemplateDownload}
            >
              CSVテンプレートをダウンロード
            </SymbolOutlineButton>
            <Text fontSize="11px" color="#718096" mt="4px">
              先頭に「アクション」列があります（任意）。続く列はテンプレートと同じ順序です。アクション列ありは
              {FUNNEL_IMPORT_COLUMN_COUNT_WITH_ACTION}
              列、アクション列なしは
              {FUNNEL_IMPORT_COLUMN_COUNT_WITHOUT_ACTION}
              列です。アップロードするCSVは、列の構成がどちらでも利用できます。
            </Text>
          </FormControl>
          <FormControl as="div" label="CSVファイル">
            <HStack alignItems="center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                // eslint-disable-next-line react/forbid-dom-props
                style={{ display: "none" }}
              />
              <SymbolOutlineButton
                icon={<FolderIcon />}
                onClick={handleClickFileInput}
              >
                ファイルを選択
              </SymbolOutlineButton>
              {fileName ? <StyledFileName>{fileName}</StyledFileName> : null}
            </HStack>
            <StyledLimitText>
              ※ 最大{IMPORT_MAX_FUNNELS}件まで一括インポートできます
            </StyledLimitText>
          </FormControl>
        </FormControlGroup>
      </StyledSection>

      <Alert status="info" variant="left-accent" mb="16px" borderRadius="4px">
        <Box>
          <AlertTitle fontSize="14px" mb="8px">
            インポート時の注意
          </AlertTitle>
          <AlertDescription fontSize="13px">
            <UnorderedList spacing="6px" pl="4px" ml="12px">
              <ListItem>
                「アクション」列は任意です（テンプレートでは先頭列として含まれます）。一覧エクスポートのように列がないCSVでもアップロードできます。アクション列がない、またはセルが空欄のときは、CSVのファネルIDが空なら登録、値が入っていれば編集として解釈します（サーバー上の有無は見ません）。列に明示した場合は登録・編集や
                CREATE／UPDATE などの別表記が使えます。
              </ListItem>
              <ListItem>
                「登録」は新規作成です。「編集」を選ぶ場合はファネルIDが必要です。
              </ListItem>
              <ListItem>
                先頭行がヘッダーのときは自動で読み飛ばします（先頭セルが「アクション」で
                {FUNNEL_IMPORT_COLUMN_COUNT_WITH_ACTION}
                列、または「ファネルID」で
                {FUNNEL_IMPORT_COLUMN_COUNT_WITHOUT_ACTION}
                列）。データ行はファイル全体で列数が統一されている必要があります。
              </ListItem>
              <ListItem>
                「登録」が選ばれた行どうしで同じファネル名が連続している場合のみ、1件のファネルとしてまとまります。先頭行のファネル情報・各ページ設定が使われ、続く行は入力フォームページの列だけが追加フォームページとして結合されます。
              </ListItem>
              <ListItem>
                「編集」が選ばれた行どうしで同じファネルIDが連続している場合も同様で、先頭行を基準に続く行の入力フォームページ列だけが追加フォームページとして結合されます。
              </ListItem>
            </UnorderedList>
          </AlertDescription>
        </Box>
      </Alert>

      {csvErrors.length > 0 ? (
        <Alert
          status="error"
          variant="left-accent"
          mb="16px"
          borderRadius="4px"
        >
          <VStack alignItems="flex-start" spacing="4px">
            {csvErrors.map((err, i) => (
              <AlertDescription key={i} fontSize="13px">
                {err}
              </AlertDescription>
            ))}
          </VStack>
        </Alert>
      ) : null}

      <HStack spacing="8px" justifyContent="flex-end">
        <GhostButton onClick={onClose}>閉じる</GhostButton>
      </HStack>
    </Modal>
  );
};
